/**
 * AcceptableUse Component
 *
 * Renders the Acceptable Use Policy page for RILE Connect. This is a static
 * content page covering permitted uses, prohibited activities, organization
 * data protection rules, AI assistant usage limits, and enforcement actions.
 *
 * Displayed as a nested route within the MainPolicies layout at
 * /policies/acceptable-use.
 */
import React from 'react';

import SEO from '@/components/common/SEO';

export default function AcceptableUse() {
    return (
        <>
            <SEO
                title="Acceptable Use Policy"
                breadcrumbs={[
                    { name: 'Home', path: '/' },
                    { name: 'Policies', path: '/policies' },
                    {
                        name: 'Acceptable Use',
                        path: '/policies/acceptable-use',
                    },
                ]}
            />
            <div className="acceptable-use-policy">
                {/* Policy header and last-updated date */}
                <h2 className="text-primary mb-4">Acceptable Use Policy</h2>
                <p className="text-muted mb-4">
                    <em>Last Updated: January 2026</em>
                </p>

                {/* Policy sections: permitted use, prohibited activities,
                    data protection, AI usage, enforcement, and contact */}
                <p>
                    This Acceptable Use Policy governs your use of RILE Connect,
                    including the organization directory, ecosystem data, and
                    AI-powered assistant. By using our platform, you agree to
                    comply with this policy.
                </p>

                <h5 className="mt-4 mb-3 fw-bold">Permitted Use</h5>
                <ul>
                    <li>
                        Browsing and searching the organization directory for
                        personal, educational, or professional purposes
                    </li>
                    <li>
                        Reasonable use of the AI assistant for mentor matching,
                        ecosystem navigation, and resource discovery
                    </li>
                    <li>
                        Onboarding your organization and maintaining your
                        organization profile
                    </li>
                    <li>
                        Sharing insights and information learned from the
                        platform with proper attribution
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">Prohibited Activities</h5>
                <p>You may NOT:</p>
                <ul>
                    <li>
                        Use automated tools, bots, scrapers, or crawlers to
                        access the platform or extract data without written
                        permission
                    </li>
                    <li>
                        Programmatically access the AI assistant or any platform
                        API without prior authorization
                    </li>
                    <li>
                        Bulk download, harvest, or systematically collect
                        organization profiles, contact information, or
                        relationship data from the directory
                    </li>
                    <li>
                        Circumvent, disable, or interfere with rate limits,
                        usage quotas, or access controls
                    </li>
                    <li>
                        Create multiple accounts to evade usage restrictions or
                        bans
                    </li>
                    <li>
                        Use organization or ecosystem data for unsolicited
                        marketing, spam, or commercial solicitation
                    </li>
                    <li>
                        Attempt to extract, reverse-engineer, or replicate the
                        AI assistant, its responses, or the underlying graph
                        data
                    </li>
                    <li>
                        Submit false or misleading information during
                        organization onboarding
                    </li>
                    <li>
                        Use the platform to generate content that is harmful,
                        misleading, or illegal
                    </li>
                    <li>
                        Impersonate other users, organizations, or misrepresent
                        your identity or affiliations
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">
                    Organization Data Protection
                </h5>
                <p>
                    The RILE Connect ecosystem directory contains information
                    contributed by participating organizations. This data is
                    provided to facilitate legitimate connections and
                    collaboration. You agree to:
                </p>
                <ul>
                    <li>
                        Use organization contact information only for purposes
                        related to STEM ecosystem collaboration
                    </li>
                    <li>
                        Respect the relationship data and not redistribute it in
                        bulk or aggregate form
                    </li>
                    <li>
                        Not use the platform to build competing databases or
                        directories without authorization
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">AI Assistant Usage</h5>
                <ul>
                    <li>
                        Users are subject to daily message limits based on
                        account type
                    </li>
                    <li>Limits reset at midnight UTC each day</li>
                    <li>
                        The AI assistant is provided for ecosystem navigation,
                        mentor matching, and resource discovery only
                    </li>
                    <li>
                        Do not attempt to manipulate the assistant into
                        providing information outside its intended scope
                    </li>
                    <li>
                        Exceeding limits may result in temporary access
                        restrictions
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">Enforcement</h5>
                <p>Violations of this policy may result in:</p>
                <ul>
                    <li>Temporary or permanent account suspension</li>
                    <li>Removal of access to the AI assistant</li>
                    <li>
                        Removal of your organization from the ecosystem
                        directory
                    </li>
                    <li>
                        Reporting to appropriate authorities if legally required
                    </li>
                    <li>
                        Legal action in cases of significant abuse or damages
                    </li>
                </ul>

                <h5 className="mt-4 mb-3 fw-bold">Contact</h5>
                <p>
                    Questions about this policy? Contact us at{' '}
                    <a href="mailto:contact@rileconnect.org">
                        contact@rileconnect.org
                    </a>
                </p>
            </div>
        </>
    );
}
